import { action, Action, computed, Computed, thunk, Thunk } from 'easy-peasy';
import { Firebase } from 'services';
import {
  createTransfer,
  getTransferPurposes,
} from 'services/firebase/transfers';

import { IRateContract, ITransfer, ITransferInput } from 'types';
import { Notify } from 'utils';

export interface TransfersStateModel {
  transfers: ITransfer[];
  transfersByRateContract: Computed<
    TransfersStateModel,
    (rateContractId: IRateContract['id'] | undefined) => ITransfer[]
  >;
  transferById: Computed<
    TransfersStateModel,
    (rateContractId?: ITransfer['id']) => ITransfer | null
  >;
  setState: Action<TransfersStateModel, [string, any]>;
  subscribeToTransfers: Thunk<
    TransfersStateModel,
    Omit<Firebase.SubscribeToTransfersParams, 'callback'>,
    null,
    object,
    (() => void) | undefined
  >;
  createTransfer: Thunk<TransfersStateModel, ITransferInput>;
  getTransferPurposes: Thunk<
    TransfersStateModel,
    Firebase.GetTransferPurposesParams
  >;
  transfersByBulkId: Computed<
    TransfersStateModel,
    (bulkId: string) => ITransfer[]
  >;
}

export const TransfersState: TransfersStateModel = {
  transfers: [],
  transfersByRateContract: computed(
    [(state) => state.transfers],
    (transfers) => (rateContractId) =>
      transfers.filter((item) => item.contractId === rateContractId)
  ),
  transferById: computed(
    [(state) => state.transfers],
    (transfers) => (transferId) =>
      transfers.find((item) => item.id === transferId) || null
  ),
  setState: action((state, payload) => {
    const [prop, to] = payload;
    state[prop] = to;
  }),
  subscribeToTransfers: thunk(({ setState }, payload) => {
    const subscriber = Firebase.subscribeToTransfers({
      ...payload,
      callback: (rateContracts) => setState(['transfers', rateContracts]),
    });

    return subscriber;
  }),
  createTransfer: thunk(async (actions, payload) => {
    const data = await createTransfer(payload);

    return data;
  }),
  getTransferPurposes: thunk(async (actions, payload) => {
    try {
      const { data } = await getTransferPurposes(payload);

      if (data.success) {
        return data;
      } else {
        Notify.error(data.message ?? '');
      }
    } catch (error) {
      console.warn(error);
    }
  }),
  transfersByBulkId: computed(
    [(state) => state.transfers],
    (transfers) => (bulkId) =>
      transfers.filter((item) => item.bulkPaymentId === bulkId)
  ),
};
